import React, { useContext, useState } from "react";
import styled from "styled-components";
import {
    ErrorText,
    FormField,
    PortalFormDescription,
    PortalFormFieldGroup,
    PortalFormLabel,
    portalColors
} from "src/components";
import RichTextEditor from "src/components/RichTextEditor/RichTextEditor";
import ScheduleOrSendNow from "./ScheduleSendNow";
import Recipients from "./Recipients";
import HeaderImage from "./HeaderImage";
import Attachments from "./Attachments";
import { NewCampaignContext } from "../NewCampaignContext";
import TestEmail from "../TestEmail";
import { EditAudienceSelection } from "../EditAudienceSelection";

const FieldStack = styled.div`
    display: grid;
    gap: 1rem;
    min-width: 0;
`;

const TextInput = styled(FormField)`
    height: 46px;
    width: 100%;
    margin: 0;
    border: 1px solid ${p => p.$hasError ? portalColors.danger : portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    box-shadow: none;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.938rem;
    font-weight: 400;
    text-align: left;

    ::placeholder {
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 0.938rem;
        font-weight: 400;
        text-align: left;
    }

    &:focus {
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
        outline: none;
    }
`;

const RichTextEditorWrap = styled.div`
    min-width: 0;

    .ql-toolbar,
    .ql-container {
        border-color: ${p => p.$hasError ? portalColors.danger : portalColors.borderStrong} !important;
    }

    .ql-toolbar {
        border-radius: 8px 8px 0 0;
    }

    .ql-container {
        border-radius: 0 0 8px 8px;
        font-family: Manrope, Helvetica, sans-serif;
    }
`;

export default function LeftSideContainer(props) {
    const { validateForSendTestEmail } = props;
    const {
        campaignName,
        setCampaignName,
        selectionEditDialogShown,
        emailSubject,
        setEmailSubject,
        sendType,
        setSendType,
        attachments,
        setAttachments,
        sendSchedule,
        setSendSchedule,
        setEmailContent,
        errors,
        setErrors,
        emailContent
    } = useContext(NewCampaignContext);
    const [showTestEmails, setShowTestEmails] = useState(false);

    const onAttachmentsChange = (file, result) => {
        setAttachments((prev) => [
            ...prev,
            {
                name: file.name,
                file,
                data: result,
            },
        ]);
    };

    const onCampaignNameChange = (e) => {
        setCampaignName(e.target.value);
        setErrors({ ...errors, campaignName: "" });
    };

    const onSubjectChange = (e) => {
        setEmailSubject(e.target.value);
        setErrors({ ...errors, emailSubject: "" });
    };

    return (
        <>
            <FieldStack>
                <PortalFormFieldGroup>
                    <PortalFormLabel>
                        Campaign Name
                    </PortalFormLabel>
                    <PortalFormDescription>
                        Give your campaign a clear name to help you track and manage it later.
                        This name will not be shown in emails sent to your contacts.
                    </PortalFormDescription>
                    <TextInput
                        type="text"
                        placeholder="Enter a name for your campaign."
                        onChange={onCampaignNameChange}
                        maxLength="100"
                        value={campaignName}
                        name="campaignName"
                        $hasError={!!errors.campaignName}
                    />
                    <ErrorText>{errors.campaignName}</ErrorText>
                </PortalFormFieldGroup>

                <PortalFormFieldGroup>
                    <PortalFormLabel>
                        Email Subject
                    </PortalFormLabel>
                    <PortalFormDescription>
                        Write the subject line that your audience will see in their inbox.
                    </PortalFormDescription>
                    <TextInput
                        type="text"
                        placeholder="Write the subject line that your audience will see in their inbox."
                        onChange={onSubjectChange}
                        maxLength="100"
                        value={emailSubject}
                        name="emailSubject"
                        $hasError={!!errors.emailSubject}
                    />
                    <ErrorText>{errors.emailSubject}</ErrorText>
                </PortalFormFieldGroup>

                <Recipients />
                <HeaderImage />

                <PortalFormFieldGroup>
                    <PortalFormLabel>
                        Email Content
                    </PortalFormLabel>
                    <PortalFormDescription>
                        Compose the body copy, links and formatting for this campaign.
                    </PortalFormDescription>
                    <RichTextEditorWrap $hasError={!!errors.emailContent}>
                        <RichTextEditor
                            editorHeight="300px"
                            setContent={setEmailContent}
                            error={errors.emailContent}
                            initialValue={emailContent}
                            placeholder="Compose the body of your email."
                        />
                    </RichTextEditorWrap>
                    <ErrorText>{errors.emailContent}</ErrorText>
                </PortalFormFieldGroup>

                <Attachments
                    onAttachmentsChange={onAttachmentsChange}
                    attachments={attachments}
                    setAttachments={setAttachments}
                />

                <ScheduleOrSendNow
                    sendType={sendType}
                    setSendType={setSendType}
                    sendSchedule={sendSchedule}
                    setSendSchedule={setSendSchedule}
                    errors={errors}
                    setErrors={setErrors}
                    setShowTestEmails={setShowTestEmails}
                    validateForSendTestEmail={validateForSendTestEmail}
                />
            </FieldStack>
            {showTestEmails && (
                <TestEmail setShowTestEmails={setShowTestEmails} />
            )}
            { selectionEditDialogShown &&
                <EditAudienceSelection />
            }
        </>
    );
}
